import React, { useState } from "react";
import { useIntl } from "react-intl";
import { observer } from "mobx-react-lite";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Close from "@mui/icons-material/Close";
import LoadingButton from "@mui/lab/LoadingButton";
import dayjs from "dayjs";
import { Plan } from "../../services/PlansService";
import { SurveyFeedback } from "../../services/SurveyService";

interface SurveyDialogProps {
    open: boolean;
    plan: Plan;
    isPaid: boolean;
    userId: string;
    onDismiss: () => void;
    onSubmit: (feedback: SurveyFeedback) => Promise<void>;
}

const SurveyDialog: React.FC<SurveyDialogProps> = observer(({
    open,
    plan,
    isPaid,
    userId,
    onDismiss,
    onSubmit,
}) => {
    const intl = useIntl();

    const [step, setStep] = useState(1);
    const [step1Answer, setStep1Answer] = useState<string | null>(null);
    const [mostHelpful, setMostHelpful] = useState("");
    const [missingOrOff, setMissingOrOff] = useState("");
    const [improvementArea, setImprovementArea] = useState<string | null>(null);
    const [upgradeBlocker, setUpgradeBlocker] = useState<string | null>(null);
    const [otherFeedback, setOtherFeedback] = useState("");
    const [missingFeaturesText, setMissingFeaturesText] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const lastStep = isPaid ? 2 : 3;

    const step1Options = [
        { value: "very_close", label: intl.formatMessage({ id: "survey.step1.veryClose" }) },
        { value: "some_differences", label: intl.formatMessage({ id: "survey.step1.someDifferences" }) },
        { value: "not_close", label: intl.formatMessage({ id: "survey.step1.notClose" }) },
        { value: "didnt_follow", label: intl.formatMessage({ id: "survey.step1.didntFollow" }) },
    ];

    const improvementAreaOptions = [
        { value: "lowConfidencePacing", label: intl.formatMessage({ id: "survey.improvement.lowConfidencePacing" }) },
        { value: "unclearCourse", label: intl.formatMessage({ id: "survey.improvement.unclearCourse" }) },
        { value: "hardToAdjust", label: intl.formatMessage({ id: "survey.improvement.hardToAdjust" }) },
        { value: "missingSharing", label: intl.formatMessage({ id: "survey.improvement.missingSharing" }) },
        { value: "justExploring", label: intl.formatMessage({ id: "survey.improvement.justExploring" }) },
        { value: "other", label: intl.formatMessage({ id: "survey.improvement.other" }) },
    ];

    const upgradeBlockerOptions = [
        { value: "justExploring", label: intl.formatMessage({ id: "survey.upgradeBlocker.justExploring" }) },
        { value: "tooEarly", label: intl.formatMessage({ id: "survey.upgradeBlocker.tooEarly" }) },
        { value: "price", label: intl.formatMessage({ id: "survey.upgradeBlocker.price" }) },
        { value: "missingFeatures", label: intl.formatMessage({ id: "survey.upgradeBlocker.missingFeatures" }) },
        { value: "notSure", label: intl.formatMessage({ id: "survey.upgradeBlocker.notSure" }) },
    ];

    const isNextDisabled =
        (step === 1 && !step1Answer) ||
        (step === 2 && !isPaid && !improvementArea);

    const handleNext = () => setStep((s) => s + 1);

    const handleBack = () => setStep((s) => s - 1);

    const handleSubmit = async () => {
        if (!step1Answer) return;
        setIsSubmitting(true);
        try {
            await onSubmit({
                userId,
                planId: plan.id,
                planDate: plan.date,
                submittedAt: new Date(),
                step1Answer,
                mostHelpful: isPaid ? mostHelpful || null : null,
                missingOrOff: isPaid ? missingOrOff || null : null,
                improvementArea: !isPaid ? improvementArea : null,
                upgradeBlocker: !isPaid ? upgradeBlocker : null,
                otherFeedback: !isPaid && improvementArea === "other" ? otherFeedback || null : null,
                missingFeaturesText: !isPaid && upgradeBlocker === "missingFeatures" ? missingFeaturesText || null : null,
                userType: isPaid ? "paid" : "free",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onDismiss} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                    <Box>
                        <Typography variant="h6">
                            {intl.formatMessage({ id: "survey.title" })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {plan.name} · {dayjs(plan.date).locale(intl.locale).format("ll")}
                        </Typography>
                    </Box>
                    <IconButton onClick={onDismiss} size="small" sx={{ mt: -0.5 }} aria-label={intl.formatMessage({ id: "action.close" })}>
                        <Close fontSize="small" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                {step === 1 && (
                    <Stack spacing={1.5} mt={0.5}>
                        <Typography variant="body2" color="text.secondary">
                            {intl.formatMessage({ id: "survey.step1.question" })}
                        </Typography>
                        <ToggleButtonGroup
                            orientation="vertical"
                            value={step1Answer}
                            exclusive
                            onChange={(_, value) => { if (value) setStep1Answer(value); }}
                            fullWidth
                        >
                            {step1Options.map((opt) => (
                                <ToggleButton
                                    key={opt.value}
                                    value={opt.value}
                                    sx={{ justifyContent: "flex-start", textTransform: "none" }}
                                >
                                    {opt.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Stack>
                )}

                {step === 2 && isPaid && (
                    <Stack spacing={2} mt={0.5}>
                        <TextField
                            label={intl.formatMessage({ id: "survey.step2paid.mostHelpful" })}
                            placeholder={intl.formatMessage({ id: "survey.optional" })}
                            multiline
                            rows={2}
                            value={mostHelpful}
                            onChange={(e) => setMostHelpful(e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label={intl.formatMessage({ id: "survey.step2paid.missingOrOff" })}
                            placeholder={intl.formatMessage({ id: "survey.optional" })}
                            multiline
                            rows={2}
                            value={missingOrOff}
                            onChange={(e) => setMissingOrOff(e.target.value)}
                            fullWidth
                        />
                    </Stack>
                )}

                {step === 2 && !isPaid && (
                    <Stack spacing={1.5} mt={0.5}>
                        <Typography variant="body2" color="text.secondary">
                            {intl.formatMessage({ id: "survey.step2free.improvementQuestion" })}
                        </Typography>
                        <ToggleButtonGroup
                            orientation="vertical"
                            value={improvementArea}
                            exclusive
                            onChange={(_, value) => { if (value) setImprovementArea(value); }}
                            fullWidth
                        >
                            {improvementAreaOptions.map((opt) => (
                                <ToggleButton
                                    key={opt.value}
                                    value={opt.value}
                                    sx={{ justifyContent: "flex-start", textTransform: "none" }}
                                >
                                    {opt.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                        {improvementArea === "other" && (
                            <TextField
                                label={intl.formatMessage({ id: "survey.step2free.otherFeedback" })}
                                placeholder={intl.formatMessage({ id: "survey.optional" })}
                                multiline
                                rows={2}
                                value={otherFeedback}
                                onChange={(e) => setOtherFeedback(e.target.value)}
                                fullWidth
                            />
                        )}
                    </Stack>
                )}

                {step === 3 && !isPaid && (
                    <Stack spacing={1.5} mt={0.5}>
                        <Typography variant="body2" color="text.secondary">
                            {intl.formatMessage({ id: "survey.step2free.upgradeBlockerQuestion" })}
                        </Typography>
                        <ToggleButtonGroup
                            orientation="vertical"
                            value={upgradeBlocker}
                            exclusive
                            onChange={(_, value) => setUpgradeBlocker(value)}
                            fullWidth
                        >
                            {upgradeBlockerOptions.map((opt) => (
                                <ToggleButton
                                    key={opt.value}
                                    value={opt.value}
                                    sx={{ justifyContent: "flex-start", textTransform: "none" }}
                                >
                                    {opt.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                        {upgradeBlocker === "missingFeatures" && (
                            <TextField
                                label={intl.formatMessage({ id: "survey.upgradeBlocker.missingFeaturesText" })}
                                placeholder={intl.formatMessage({ id: "survey.optional" })}
                                multiline
                                rows={2}
                                value={missingFeaturesText}
                                onChange={(e) => setMissingFeaturesText(e.target.value)}
                                fullWidth
                            />
                        )}
                    </Stack>
                )}
            </DialogContent>

            <DialogActions>
                {step === 1 && (
                    <>
                        <Button onClick={onDismiss} color="inherit">
                            {intl.formatMessage({ id: "survey.skip" })}
                        </Button>
                        <Button variant="contained" onClick={handleNext} disabled={isNextDisabled}>
                            {intl.formatMessage({ id: "nav.next" })}
                        </Button>
                    </>
                )}
                {step > 1 && step < lastStep && (
                    <>
                        <Button onClick={handleBack} color="inherit">
                            {intl.formatMessage({ id: "nav.back" })}
                        </Button>
                        <Button variant="contained" onClick={handleNext} disabled={isNextDisabled}>
                            {intl.formatMessage({ id: "nav.next" })}
                        </Button>
                    </>
                )}
                {step === lastStep && (
                    <>
                        <Button onClick={handleBack} color="inherit">
                            {intl.formatMessage({ id: "nav.back" })}
                        </Button>
                        <LoadingButton
                            variant="contained"
                            onClick={handleSubmit}
                            loading={isSubmitting}
                        >
                            {intl.formatMessage({ id: "survey.submit" })}
                        </LoadingButton>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
});

export default SurveyDialog;
